// src/index.js
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import "./index.css";
import App from "./App";
import AnimatedBackground from "./components/AnimatedBackground";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import { msalInstance } from "./auth/msal";

// Initialize MSAL, handle redirect/popup response, then render the app
const initializeApp = async () => {
  try {
    // Ensure MSAL is initialized before rendering
    await msalInstance.initialize();
    console.log("MSAL initialized successfully");

    // Process auth response from redirect/popup (e.g. #code=... in URL)
    const response = await msalInstance.handleRedirectPromise();

    if (response?.account) {
      // If this page loaded as part of an auth redirect/popup, mark that account as active
      msalInstance.setActiveAccount(response.account);

      // The Azure-registered redirectUri may land us on "/" (Home), which has
      // no auth-then-navigate logic. Re-write the URL to "/loginNew" so the
      // LoginNew page's useEffect picks up the new auth state and routes the
      // user to /dashboard (or /global-admin based on role).
      try {
        const path = window.location.pathname;
        if (path === "/" || path === "") {
          window.history.replaceState({}, "", "/loginNew");
        }
      } catch (_) {
        // Non-fatal; the AuthContext will still authenticate the user.
      }
    } else {
      // On normal loads, ensure an active account is set if one already exists
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    }
  } catch (error) {
    console.error("MSAL init/redirect handling failed:", error);
  }
  
  // Render the app regardless of MSAL initialization status
  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <AnimatedBackground />
      <MsalProvider instance={msalInstance}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <ErrorBoundary>
            <App />
            <ScrollToTop />
          </ErrorBoundary>
        </BrowserRouter>
      </MsalProvider>
    </React.StrictMode>
  );
};

// Start the app initialization
initializeApp();
